// ============================================================================
// Frontend Types — mirrors API types
// ============================================================================

// ── Caption Settings Types ──────────────────────────────────────

export type CaptionPosition = 'bottom' | 'center' | 'top';
export type CaptionTextCase = 'auto' | 'uppercase' | 'original';
export type CaptionEmojiDensity = 'none' | 'light' | 'full';

export interface CaptionSettings {
  engine: 'captions_ai';
  templateId: string;
  position: CaptionPosition;
  textCase: CaptionTextCase;
  emojiDensity: CaptionEmojiDensity;
  highlightCurrentWord: boolean;
  profanityFilter: boolean;
  maxWordsPerCaption: number;
}

export interface UploadStitchGroupMetadata {
  enabled: boolean;
  groupId: string;
  sourceIndex: number;
  sourceCount: number;
  sharedCaptionSettings: CaptionSettings;
}

export interface SourceAsset {
  s3Key: string;
  filename: string;
  fileSize: number;
  contentType?: string;
  uploadedAt?: string;
  trim?: {
    startSec: number;
    endSec: number;
    durationSec?: number;
  };
}

export const DEFAULT_CAPTION_SETTINGS: CaptionSettings = {
  engine: 'captions_ai',
  templateId: 'ctpl_DxflLOnuKkb198FNdI9E',
  position: 'bottom',
  textCase: 'auto',
  emojiDensity: 'light',
  highlightCurrentWord: true,
  profanityFilter: false,
  maxWordsPerCaption: 6,
};

export interface CaptionTemplate {
  id: string;
  name: string;
  previewUrl?: string;
}

export const CAPTION_POSITION_OPTIONS = [
  { value: 'bottom', label: 'Bottom third' },
  { value: 'center', label: 'Centered' },
  { value: 'top', label: 'Top third' },
] as const;

export const CAPTION_TEXT_CASE_OPTIONS = [
  { value: 'auto', label: 'Auto / natural case' },
  { value: 'uppercase', label: 'Uppercase emphasis' },
  { value: 'original', label: 'Keep original casing' },
] as const;

export const CAPTION_EMOJI_DENSITY_OPTIONS = [
  { value: 'none', label: 'No emojis' },
  { value: 'light', label: 'Light accents' },
  { value: 'full', label: 'Frequent emojis' },
] as const;

// ── Core Types ──────────────────────────────────────────────────

export type MediaType = 'video' | 'image';
export type ContentStatus = 'uploaded' | 'processing' | 'transcribed' | 'clips_extracted' | 'ready' | 'error';
export type Platform = 'linkedin' | 'twitter' | 'tiktok' | 'instagram' | 'youtube_shorts' | 'youtube' | 'snapchat';
export type DraftStatus = 'pending' | 'draft' | 'in_review' | 'approved' | 'scheduled' | 'published' | 'rejected';

export interface TranscriptSegment {
  start: number; // seconds
  end: number; // seconds
  text: string;
}

export type ClipStatus = 'base' | 'captioned';

export interface ContentClip {
  platform: string;
  s3Key: string;
  status: ClipStatus;
  downloadUrl?: string;
  segments?: Array<{ start: number; end: number }>;
  reason?: string;
  updatedAt?: string;
}

// ── User Management Types ────────────────────────────────────────

export type UserRole = 'admin' | 'editor' | 'none';

export interface AppUser {
  userId: string;
  email: string;
  fullName: string;
  status: string;
  enabled: boolean;
  role: UserRole;
  groups: string[];
  createdAt: string;
  lastModified: string;
  lastLogin?: string;
}

export interface UserAttribution {
  userId: string;
  email: string;
}

export interface ActionHistoryEntry {
  action: string;
  userId: string;
  email: string;
  timestamp: string;
  notes?: string;
}

export const USER_STATUS_INFO: Record<string, { label: string; color: string; bgColor: string }> = {
  CONFIRMED: { label: 'Active', color: 'text-green-700', bgColor: 'bg-green-100' },
  FORCE_CHANGE_PASSWORD: { label: 'Password Reset', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  UNCONFIRMED: { label: 'Unconfirmed', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  RESET_REQUIRED: { label: 'Reset Required', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  DISABLED: { label: 'Suspended', color: 'text-red-700', bgColor: 'bg-red-100' },
  UNKNOWN: { label: 'Unknown', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

export const ROLE_INFO: Record<UserRole, { label: string; color: string; bgColor: string }> = {
  admin: { label: 'Admin', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  editor: { label: 'Editor', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  none: { label: 'No Role', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

export interface ContentItem {
  contentId: string;
  title: string;
  description: string;
  sourceUrl: string;
  sourceUrls?: string[];
  sourceAssets?: SourceAsset[];
  sourceDownloadUrl?: string;
  sourceDownloadUrls?: string[];
  denoisedSourceKey?: string;
  denoisedDownloadUrl?: string;
  mediaType: MediaType;
  status: ContentStatus;
  thumbnailUrl?: string;
  thumbnailDownloadUrl?: string;
  duration?: number;
  transcript?: string;
  transcriptTimestamps?: TranscriptSegment[];
  clipsArray?: ContentClip[];
  clipStatus?: 'base_clips_ready' | 'captioned_clips_ready';
  uploadedBy: string;
  uploadedByUser?: UserAttribution;
  uploadedAt: string;
  metadata: Record<string, unknown>;
  drafts?: Draft[];
}

export interface Draft {
  draftId: string;
  contentId: string;
  platform: Platform;
  status: DraftStatus;
  textContent: string;
  mediaUrls: string[];
  mediaDownloadUrls?: string[];
  platformMetadata: PlatformMetadata;
  scheduledAt?: string;
  createdByUser?: UserAttribution;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  actionHistory?: ActionHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface PlatformMetadata {
  hashtags?: string[];
  mentions?: string[];
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  aspectRatio?: string;
  maxDuration?: number;
  [key: string]: unknown;
}

export interface PlatformConfig {
  platform: Platform;
  displayName: string;
  maxTextLength: number;
  supportedMediaTypes: MediaType[];
  aspectRatios: string[];
  maxDurationSeconds?: number;
  maxFileSizeMb: number;
  features: string[];
}

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  nextToken?: string;
}

export interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'registering' | 'complete' | 'error';
  error?: string;
  s3Key?: string;
  contentId?: string;
}

// ── Batch Job Types ─────────────────────────────────────────────────

export type JobType = 'transcribe' | 'extract_clips' | 'denoise_audio' | 'caption_burnin' | 'generate_drafts';
export type JobStatusType = 'pending' | 'queued' | 'running' | 'completed' | 'failed';

export interface JobStatus {
  contentId: string;
  jobType: JobType;
  status: JobStatusType;
  batchJobId?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  outputs?: Record<string, unknown>;
}

export const JOB_TYPE_INFO: Record<JobType, { label: string; order: number }> = {
  transcribe: { label: 'Transcribe', order: 0 },
  denoise_audio: { label: 'Denoise Audio', order: 1 },
  extract_clips: { label: 'Extract Clips', order: 2 },
  caption_burnin: { label: 'Caption Burn-in', order: 3 },
  generate_drafts: { label: 'Generate Drafts', order: 4 },
};

export const PIPELINE_JOB_ORDER: JobType[] = [
  'transcribe',
  'denoise_audio',
  'extract_clips',
  'caption_burnin',
  'generate_drafts',
];

// ── Ideas Types ─────────────────────────────────────────────────

export type IdeaCategory =
  | 'market-trends'
  | 'first-time-buyers'
  | 'refinancing'
  | 'industry-news'
  | 'tips-tricks'
  | 'behind-scenes'
  | 'client-stories';

export type IdeaStatus = 'active' | 'archived' | 'used';

export interface IdeaSource {
  title: string;
  url: string;
  snippet: string;
}

export interface PlatformStrategyDetail {
  approach: string;
  whyItWorks: string;
  bestFormat: string;
}

export interface FilmingPlanDetail {
  scriptOutline?: string;
  hostNotes?: string;
  cameraAngles?: string;
  setting?: string;
  props?: string;
}

export interface Idea {
  ideaId: string;
  title: string;
  description: string;
  category: IdeaCategory;
  tags: string[];
  sources: IdeaSource[];
  platformStrategy: Record<string, PlatformStrategyDetail>;
  filmingPlan: string | FilmingPlanDetail | null;
  imageUrls: string[];
  imageDownloadUrls?: string[];
  imagePrompts: string[];
  status: IdeaStatus;
  createdAt: string;
  updatedAt: string;
  generatedBy: string;
  searchText?: string;
}

export interface IdeaConfig {
  enabled: boolean;
  frequencyHours: number;
  model: string;
  defaultFocus: string;
}

export interface GenerationStatus {
  running: boolean;
  generationId: string | null;
  progress: string | null;
  startedAt: string | null;
  completedAt?: string | null;
  lastError?: string | null;
}

export interface ImageGenerationInputUpload {
  s3Key: string;
  filename?: string;
  contentType?: string;
  previewUrl?: string;
  downloadUrl?: string;
}

export interface ImageGeneration {
  imageGenerationId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByEmail?: string;
  prompt: string;
  mode: 'image_only' | 'text_plus_image';
  aspectRatio: string;
  size: string;
  count: number;
  status: 'queued' | 'running' | 'completed' | 'failed';
  inputImageUrls: ImageGenerationInputUpload[];
  outputImageUrls?: ImageGenerationInputUpload[];
  error?: string;
  model?: string;
}

export const IDEA_CATEGORY_INFO: Record<IdeaCategory, { label: string; color: string; bgColor: string }> = {
  'market-trends': { label: 'Market Trends', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  'first-time-buyers': { label: 'First-Time Buyers', color: 'text-green-700', bgColor: 'bg-green-100' },
  'refinancing': { label: 'Refinancing', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  'industry-news': { label: 'Industry News', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  'tips-tricks': { label: 'Tips & Tricks', color: 'text-teal-700', bgColor: 'bg-teal-100' },
  'behind-scenes': { label: 'Behind the Scenes', color: 'text-pink-700', bgColor: 'bg-pink-100' },
  'client-stories': { label: 'Client Stories', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
};

export const PLATFORM_INFO: Record<string, { name: string; icon: string; color: string }> = {
  linkedin: { name: 'LinkedIn', icon: '💼', color: 'bg-blue-600' },
  twitter: { name: 'X / Twitter', icon: '𝕏', color: 'bg-black' },
  tiktok: { name: 'TikTok', icon: '🎵', color: 'bg-pink-500' },
  instagram: { name: 'Instagram Reels', icon: '📸', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  instagram_reels: { name: 'Instagram Reels', icon: '📸', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  youtube_shorts: { name: 'YouTube Shorts', icon: '▶️', color: 'bg-red-600' },
  youtube: { name: 'YouTube', icon: '📺', color: 'bg-red-700' },
  snapchat: { name: 'Snapchat', icon: '👻', color: 'bg-yellow-400' },
};

export const DRAFT_STATUS_INFO: Record<DraftStatus, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Pending Review', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  draft: { label: 'Draft', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  in_review: { label: 'In Review', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  approved: { label: 'Approved', color: 'text-green-700', bgColor: 'bg-green-100' },
  scheduled: { label: 'Scheduled', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  published: { label: 'Published', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  rejected: { label: 'Rejected', color: 'text-red-700', bgColor: 'bg-red-100' },
};

export const CONTENT_STATUS_INFO: Record<string, { label: string; color: string; bgColor: string }> = {
  uploaded: { label: 'Uploaded', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  processing: { label: 'Processing', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  transcribed: { label: 'Transcribed', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  clips_extracted: { label: 'Clips Ready', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  ready: { label: 'Ready', color: 'text-green-700', bgColor: 'bg-green-100' },
  error: { label: 'Error', color: 'text-red-700', bgColor: 'bg-red-100' },
};
